import type { ReactNode } from "react";

/** The @strat/ui design system's markup, written out: the stylesheet is shared, the components are not exported yet (plan §3.4). */
export function Card({
  title,
  actions,
  children,
  flush,
  id,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  flush?: boolean;
  /** Anchor for in-page links (Settings' section nav). */
  id?: string;
}) {
  return (
    <section className="card" id={id}>
      {(title || actions) && (
        <header className="card__header">
          <span className="card__title">{title}</span>
          {actions && <div className="row">{actions}</div>}
        </header>
      )}
      <div className={flush ? "card__body card__body--flush" : "card__body"}>{children}</div>
    </section>
  );
}

export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon: string;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <div className="empty-state__icon">{icon}</div>
      <div className="empty-state__title">{title}</div>
      {children && <div className="empty-state__body">{children}</div>}
      {action}
    </div>
  );
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="stat">
      <div className="stat__label" title={hint}>
        {label}
      </div>
      <div className="stat__value">{value}</div>
    </div>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
    </label>
  );
}

export function PageHead({ title, children }: { title: ReactNode; children?: ReactNode }) {
  return (
    <div className="page__head">
      <h1 className="page__title">{title}</h1>
      {children}
    </div>
  );
}

export function Alert({ tone, children }: { tone: "error" | "ok"; children: ReactNode }) {
  return (
    <div className={`alert alert--${tone}`} role={tone === "error" ? "alert" : "status"}>
      {children}
    </div>
  );
}
